import React from 'react';
import { useTranslation } from 'react-i18next';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    rowsPerPage: number;
    setRowsPerPage: (value: number) => void;
    handlePageChange: (pageNumber: number) => void;
    disablePrevious: boolean;
    disableNext: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    rowsPerPage,
    setRowsPerPage,
    handlePageChange,
    disablePrevious,
    disableNext,
}) => {
    const { t } = useTranslation();

    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        setRowsPerPage(Number(event.target.value));
        handlePageChange(1); // Reset to first page
    };

    const goToPreviousPage = () => {
        if (!disablePrevious) {
            handlePageChange(currentPage - 1);
        }
    };

    const goToNextPage = () => {
        if (!disableNext) {
            handlePageChange(currentPage + 1);
        }
    };

    const renderPageNumbers = () => {
        const pageNumbers = [];
        const maxVisiblePages = 3;

        if (totalPages <= 6) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(renderPageButton(i));
            }
        } else {
            if (currentPage <= maxVisiblePages) {
                for (let i = 1; i <= maxVisiblePages + 1; i++) {
                    pageNumbers.push(renderPageButton(i));
                }
                pageNumbers.push(renderEllipsis());
                pageNumbers.push(renderPageButton(totalPages));
            } else if (currentPage >= totalPages - maxVisiblePages + 1) {
                pageNumbers.push(renderPageButton(1));
                pageNumbers.push(renderEllipsis());
                for (
                    let i = totalPages - maxVisiblePages + 1;
                    i <= totalPages;
                    i++
                ) {
                    pageNumbers.push(renderPageButton(i));
                }
            } else {
                pageNumbers.push(renderPageButton(1));
                pageNumbers.push(renderEllipsis());

                const startPage = currentPage - 1;
                const endPage = currentPage + 1;

                for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(renderPageButton(i));
                }

                pageNumbers.push(renderEllipsis());
                pageNumbers.push(renderPageButton(totalPages));
            }
        }

        return pageNumbers;
    };

    const renderPageButton = (pageNumber: number) => (
        <button
            key={pageNumber}
            className={`mx-1 pagination-padding py-2 border rounded ${pageNumber === currentPage ? '!bg-primary text-white' : 'bg-white text-black'}`}
            onClick={() => handlePageChange(pageNumber)}
        >
            {pageNumber}
        </button>
    );

    const renderEllipsis = () => (
        <span key="ellipsis" className="mx-1 py-2">
            ...
        </span>
    );

    return (
        <div className="mb-4 mt-5 flex justify-between items-center">
            <div>
                <select
                    id="rows-per-page"
                    className="form-select rounded"
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    aria-label="sort-data"
                >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                </select>
            </div>
            <div className="flex justify-center mx-auto">
                <button
                    className="btn bg-white text-black text-sm p-1"
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1 || disablePrevious}
                >
                    {t('user_management.pagination.previous')}
                </button>
                {renderPageNumbers()}
                <button
                    className="btn bg-white text-black text-sm p-1"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages || disableNext}
                >
                    {t('user_management.pagination.next')}
                </button>
            </div>
        </div>
    );
};

export default Pagination;
